import { defineStore } from "pinia";
import axios from "@/utils/axios";
import Swal from "sweetalert2";
import { useSharedStore } from "@/stores/shared-store";
import { useAuthStore } from "@/stores/auth-store";

const BASE_URL = `${process.env.VUE_APP_BE_URL}/api/learning-path/learning-paths`;

export const useLearningPathStore = defineStore("LearningPath", {
  state: () => ({
    learningPaths: [],
    studentPaths: [],
  }),

  actions: {
    async fetchPaths() {
      const sharedStore = useSharedStore();
      const authStore = useAuthStore();
      sharedStore.startRequest();
      try {
        const teacherId = authStore.currentUser?.id;
        if (!teacherId) {
          this.learningPaths = [];
          return;
        }

        // by-teacher + filter client-side agar path instructor lain tidak ikut tampil
        const res = await axios.get(`${BASE_URL}/by-teacher/`, {
          params: { teacher_id: teacherId },
        });
        const rows = Array.isArray(res.data) ? res.data : [];
        this.learningPaths = rows.filter(
          (p) => Number(p.teacher) === Number(teacherId)
        );
      } catch (e) {
        console.error("Gagal fetch learning paths:", e);
        this.learningPaths = [];
      } finally {
        sharedStore.endRequest();
      }
    },

    async fetchAllPaths(params = {}) {
      const sharedStore = useSharedStore();
      sharedStore.startRequest();
      try {
        const res = await axios.get(`${BASE_URL}/`, { params });
        this.learningPaths = res.data || [];
      } catch (e) {
        console.error("Gagal fetch all learning paths:", e);
      } finally {
        sharedStore.endRequest();
      }
    },

    async fetchStudentPaths() {
      const sharedStore = useSharedStore();
      const authStore = useAuthStore();
      sharedStore.startRequest();
      try {
        const res = await axios.get(`${BASE_URL}/by-student/`, {
          params: { student_id: authStore.currentUser?.id },
        });
        this.studentPaths = res.data || [];
      } catch (e) {
        console.error("Gagal fetch student paths:", e);
      } finally {
        sharedStore.endRequest();
      }
    },

    async createPath(payload) {
      const sharedStore = useSharedStore();
      const authStore = useAuthStore();
      sharedStore.startRequest();
      try {
        await axios.post(`${BASE_URL}/`, {
          teacher: authStore.currentUser?.id,
          title: payload.title,
          note: payload.note,
          items: payload.items || [],
        });
        await this.fetchPaths();
        return true;
      } catch (e) {
        console.error("Gagal membuat learning path:", e);
        Swal.fire("Error", "Gagal membuat learning path.", "error");
        return false;
      } finally {
        sharedStore.endRequest();
      }
    },

    async updatePath(pathId, payload) {
      const sharedStore = useSharedStore();
      const authStore = useAuthStore();
      sharedStore.startRequest();
      try {
        await axios.put(`${BASE_URL}/${pathId}/`, {
          teacher: authStore.currentUser?.id,
          title: payload.title,
          note: payload.note,
          items: payload.items || [],
        });
        Swal.fire("Berhasil", "Learning path berhasil diperbarui.", "success");
        await this.fetchPaths();
        return true;
      } catch (e) {
        console.error("Gagal mengupdate learning path:", e);
        Swal.fire("Error", "Gagal memperbarui learning path.", "error");
        return false;
      } finally {
        sharedStore.endRequest();
      }
    },

    async deletePath(pathId) {
      const id = Number(pathId);
      if (!Number.isFinite(id) || id <= 0) {
        Swal.fire(
          "Error",
          `ID learning path tidak valid${pathId != null ? `: ${pathId}` : "."}`,
          "error"
        );
        return;
      }
      const sharedStore = useSharedStore();
      sharedStore.startRequest();
      try {
        await axios.delete(`${BASE_URL}/${id}/`);
        await this.fetchPaths();
        Swal.fire("Berhasil", "Learning path berhasil dihapus.", "success");
      } catch (e) {
        console.error("Gagal menghapus learning path:", e);
        // Jika path sudah terhapus (double-submit / retry setelah sukses), anggap sukses.
        if (e?.response?.status === 404) {
          await this.fetchPaths();
          const stillExists = this.learningPaths.some((p) => Number(p.id) === id);
          if (!stillExists) {
            Swal.fire("Berhasil", "Learning path berhasil dihapus.", "success");
            return;
          }
        }
        const detail =
          e?.response?.data?.detail ||
          e?.response?.data?.error ||
          "Gagal menghapus learning path.";
        Swal.fire("Error", detail, "error");
      } finally {
        sharedStore.endRequest();
      }
    },

    async assignStudents(pathId, studentIds) {
      const sharedStore = useSharedStore();
      sharedStore.startRequest();
      try {
        const res = await axios.post(`${BASE_URL}/${pathId}/assign-students/`, {
          student_ids: studentIds,
        });
        Swal.fire("Berhasil", res.data.message, "success");
        await this.fetchPaths();
        return true;
      } catch (e) {
        console.error("Gagal assign students:", e);
        Swal.fire("Error", e?.response?.data?.error || "Gagal assign student.", "error");
        return false;
      } finally {
        sharedStore.endRequest();
      }
    },

    async removeStudent(pathId, studentId) {
      const sharedStore = useSharedStore();
      sharedStore.startRequest();
      try {
        const res = await axios.delete(
          `${BASE_URL}/${pathId}/remove-student/${studentId}/`
        );
        Swal.fire(
          "Berhasil",
          res.data?.message || "Student berhasil di-unassign.",
          "success"
        );
        await this.fetchPaths();
        return true;
      } catch (e) {
        console.error("Gagal unassign student:", e);
        Swal.fire(
          "Error",
          e?.response?.data?.error || "Gagal unassign student.",
          "error"
        );
        return false;
      } finally {
        sharedStore.endRequest();
      }
    },
  },
});
